import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  fetchVenueConfig,
  getDefaultVenue
} from "../APIs/Venue/GetVenueConfig";
import { useLoadingStatus } from "../context/LoadingStatusContext";
import { getBookingStages } from "../utils/booking";
import { dmnStorage } from "../utils/dmnStorage";
import { VenueConfig } from "../types/venues";
import { useAppContext } from "./useAppContext";
import { useDiaryContext } from "./useDiaryContext";
import { useGetVenues } from "./useFetchVenues";
import { FIVE_MINUTES, THIRTY_MINUTES } from "../config/queryConfig";

const useManageAPIVenueData = () => {
  const { updateStage } = useLoadingStatus();
  const { venueGroupConfig, venueUserConfig } = useAppContext();
  const { setDiaryContext, selectedVenue, isInitialising } = useDiaryContext();
  const venueGroupId = venueGroupConfig?._id;
  const queryClient = useQueryClient();

  const { allVenues } = useGetVenues(venueGroupId);

  const venueConfigQueryKey = ["venueConfig", selectedVenue];
  const {
    data: venueConfigData,
    isError: hasVenueConfigError,
    isLoading: isVenueConfigLoading,
    error: venueConfigError
  } = useQuery({
    queryKey: venueConfigQueryKey,
    queryFn: () => fetchVenueConfig(selectedVenue),
    // staleTime (5 min): serve the cached venue config immediately on remount
    //   while a background refresh keeps it current; invalidated implicitly by
    //   the 5-minute refetchInterval and by selectedVenue changing the key.
    // gcTime (30 min): held well above the 5-minute refetchInterval so a cached
    //   entry is never garbage-collected exactly when it would be reused.
    staleTime: FIVE_MINUTES,
    gcTime: THIRTY_MINUTES,
    enabled: !!selectedVenue,
    refetchInterval: FIVE_MINUTES, // 5 minutes in milliseconds
    refetchIntervalInBackground: false, // Optional: don't refetch when tab is not active
    // Seed the first render from the persisted-then-restored cache so a
    // returning user's venue config paints immediately. initialDataUpdatedAt
    // carries the restored timestamp so staleTime still drives the background
    // refresh, and an empty cache yields undefined, preserving the loading path.
    initialData: () =>
      queryClient.getQueryData<VenueConfig>(venueConfigQueryKey),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(venueConfigQueryKey)?.dataUpdatedAt
  });

  useEffect(() => {
    if (!selectedVenue) return;

    if (isVenueConfigLoading) {
      updateStage("venueConfig", "loading");
    } else if (hasVenueConfigError) {
      updateStage("venueConfig", "error", {
        message: "Failed to load venue configuration.",
        technicalDetails:
          venueConfigError instanceof Error
            ? venueConfigError.message
            : `fetchVenueConfig() failed for selectedVenue: ${selectedVenue}`
      });
    } else if (venueConfigData) {
      updateStage("venueConfig", "success");
    }
  }, [
    selectedVenue,
    isVenueConfigLoading,
    hasVenueConfigError,
    venueConfigData,
    updateStage
  ]);

  useEffect(() => {
    if (allVenues?.length === 0 && isInitialising) {
      setDiaryContext({
        selectedVenue: undefined,
        venueConfig: {},
        venues: [],
        stages: [],
        isInitialising: false
      });
      return;
    }

    if (selectedVenue || !allVenues || allVenues.length === 0) {
      return;
    }

    (async () => {
      const defaults = await getDefaultVenue({
        venues: allVenues,
        venueUserConfig
      });
      setDiaryContext({
        selectedVenue: defaults,
        venues: allVenues
      });
    })();
  }, [selectedVenue, allVenues, venueUserConfig]);

  useEffect(() => {
    if (!venueConfigData || !venueGroupConfig) {
      return;
    }

    dmnStorage.add("venueId", venueConfigData._id);
    setDiaryContext({
      venueConfig: venueConfigData,
      venues: allVenues?.filter((venue) => !!venue.title) || [],
      stages: getBookingStages({
        venueConfig: venueConfigData,
        venueGroupConfig
      })
    });
  }, [venueConfigData, venueGroupConfig, allVenues]);
};
export default useManageAPIVenueData;
